import Apptimize from '@apptimize/apptimize-web-sdk';
import * as Sentry from '@sentry/browser';

const Plugin = {
  install(Vue) {
    if (this.installed) return;
    this.experiments = {};
    this.featureFlags = {};
    this.installed = true;
    this.key = null;
    this.overrideFeatureFlags = {};
    this.signUp = false;
    this.setup = false;
    this.user = null;
    this.userOptOut = false;

    // called whenever the user is loaded from the API
    const config = (user, signUp = false) => {
      if (user.abTestOptOut) {
        this.userOptOut = true;
      };
      this.user = user;
      this.signUp = signUp;
      identify();
    };

    // called when Apptimize and the user have loaded and are ready
    const identify = () => {
      if (this.user && this.setup) {
        const newUser = Number(Apptimize.getCustomerUserId()) !== this.user.id;
        if (newUser) {
          // needs tostring or will error on ie11
          Apptimize.setCustomerUserId(this.user.id.toString());
        }

        const attributes = Apptimize.getCustomAttributes();
        attributes['User_Classification'] = this.user.classification;
        attributes['Highest Owned Product Tier'] = this.user.productTier?.formattedString;
        attributes['Subscription Purchase Order'] = this.user.subscriptionPo?.formattedString;
        if (this.signUp) {
          attributes['Registered_Time'] = Date.now();
          attributes.lockedRationaleEligible = true;
          this.signUp = false;
        }
        if (this.userOptOut) {
          attributes.userOptOut = true;
        }
        Apptimize.setCustomAttributes(attributes);
      }
    };

    // called when it is time to set up Apptimize
    const init = (productVar) => {
      if (productVar && productVar.value) {
        this.key = productVar.value;

        Apptimize.setOnApptimizeInitializedCallback(() => {
          window.apptimizeSetup = true;
          this.setup = true;
          identify();
        });

        Apptimize.setup(this.key, { log_level: 'LOG_LEVEL_ERROR' });
      }
    };

    const enabled = () => {
      if (this.userOptOut) {
        return false;
      }
      else if (this.setup) {
        return true;
      } else if (this.key) {
        Sentry.captureMessage('Apptimize not loading in time!');
      }
    };

    const checkExperiments = ({experimentName, bool, arrName, arrValue, defaultVal, int, matcher, str}) => {
      // Check if we have checked this value already and return it if so
      const cachedResult = this.experiments[experimentName];
      if (cachedResult !== undefined) {
        return cachedResult;
      }

      // Otherwise, check apptimize and log the localytics event
      let apptimizeResult;
      if (bool) {
        apptimizeResult = Apptimize.getBool(bool, defaultVal);
      } else if (arrName) {
        const apptimizeArray = Apptimize.getIntArray(arrName, defaultVal);
        apptimizeResult = apptimizeArray.includes(arrValue);
      } else if (int) {
        apptimizeResult = Apptimize.getInt(int, defaultVal);
      } else if (str) {
        apptimizeResult = Apptimize.getString(str, defaultVal);
      }

      const variant = Apptimize.getVariantInfo().find(v => matcher.test(v.getExperimentName()));
      if (variant && Vue.prototype.$analytics) {
        Vue.prototype.$analytics.events.apptimizeVariant(variant.getExperimentName(), variant.getVariantName());
      }

      // Add to the experiments object and return the value
      this.experiments[experimentName] = apptimizeResult;
      return apptimizeResult;
    };

    const checkFeatureFlags = (flag) => {
      const overridenFlag = this.overrideFeatureFlags[flag];
      if (overridenFlag !== undefined) {
        return overridenFlag;
      }

      const cachedResult = this.featureFlags[flag];
      if (cachedResult !== undefined) {
        return cachedResult;
      }

      const featureEnabled = Apptimize.isFeatureFlagEnabled(flag);
      this.featureFlags[flag] = featureEnabled;
      return featureEnabled;
    };

    const addFeatureFlagOverride = (flag, value) => {
      this.overrideFeatureFlags[flag] = value;
    };

    const removeFeatureFlagOverride = flag => {
      delete this.overrideFeatureFlags[flag];
    };

    Vue.prototype.$apptimize = {
      addFeatureFlagOverride,
      checkExperiments,
      checkFeatureFlags,
      config,
      enabled,
      init,
      root: Apptimize,
      removeFeatureFlagOverride,
    };

    // temporary for debugging
    window.$apptimize = Vue.prototype.$apptimize;
  },
};

export default Plugin;
